import type {AxiosError, InternalAxiosRequestConfig} from "axios";
import axios from "axios";
import {environment} from "../env";
import {authStore} from "../stores/init";
import {isTokenExpired, SESSION_STORAGE_PREFIX} from "../authentication/AuthTypes";

type RequestConfig = InternalAxiosRequestConfig & {_rqRefreshTried?: boolean};

function markSessionInvalidIfNeeded(error: AxiosError) {
    if (error.response?.status === 401 && isTokenExpired(error)) {
        authStore.authentication.markTokenInvalid();
    }
}

/** Djangorestframework-simplejwt style refresh (adjust if backend path differs). */
const tokenRefreshUrl = `${environment.authUrl}/token/refresh/`;

const axiosInstance = axios.create({
    baseURL: environment.materializedApiUrl,
    headers: {
        "Content-type": "application/json",
    },
});

let refreshPromise: Promise<void> | null = null;

async function runRefresh(): Promise<void> {
    const refresh = sessionStorage.getItem(`${SESSION_STORAGE_PREFIX}refresh_token`);
    if (!refresh) {
        throw new Error("queryHttp: missing refresh token");
    }
    const {data} = await axios.post<{access: string; refresh?: string}>(
        tokenRefreshUrl,
        {refresh},
        {headers: {"Content-type": "application/json"}},
    );
    authStore.authentication.applyTokenRefresh(data.access, data.refresh); // check if this is correct??
}

axiosInstance.interceptors.request.use(config => {
    const token = authStore.authentication.mithraAuthToken?.access_token;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

axiosInstance.interceptors.response.use(
    res => res,
    async (error: AxiosError) => {
        const original = error.config as RequestConfig | undefined;
        if (
            !original
            || original._rqRefreshTried
            || error.response?.status !== 401
            || !isTokenExpired(error)
            || environment.devLogin
        ) {
            return Promise.reject(error);
        }

        original._rqRefreshTried = true;

        try {
            if (!refreshPromise) {
                refreshPromise = runRefresh().finally(() => {
                    refreshPromise = null;
                });
            }
            await refreshPromise;
            const token = authStore.authentication.mithraAuthToken?.access_token;
            if (token) {
                original.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance.request(original);
        } catch {
            markSessionInvalidIfNeeded(error);
            // authStore.authentication.clearSession();
            return Promise.reject(error);
        }
    },
);

export const queryHttp = axiosInstance;
